import React, { useState } from 'react';
import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import { BusinessCenter, MonetizationOn, Receipt, ArrowForward } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import SectionHeader from './SectionHeader';
import { C, F } from '../styles/tokens';

/**
 * Per-service accent colours are intentional data — they are NOT part of the
 * global palette. Each service has its own branded highlight.
 */
const SERVICES = [
  {
    number:      '01',
    title:       'Company Registration',
    tagline:     'Turn your idea into a legal entity',
    icon:        <BusinessCenter sx={{ fontSize: 38 }} />,
    description: 'Company registration is the first step in turning your business idea into a legal entity — granting your company a distinct identity, rights, and market credibility.',
    features:    ['Private Limited', 'LLP', 'One Person Company', 'Proprietorship'],
    link:        'https://wa.me/9311817126',
    detailLink:  '/company-registration',
    accent:      '#4f8ef7',
  },
  {
    number:      '02',
    title:       'Income Tax Filing',
    tagline:     'Stay compliant, maximise your returns',
    icon:        <MonetizationOn sx={{ fontSize: 38 }} />,
    description: 'We specialise in filing returns for residents, non-residents, and expatriates — ensuring full compliance while uncovering every deduction you\'re entitled to.',
    features:    ['Individual Returns', 'Corporate Filing', 'NRI & Expat', 'Refund Claims'],
    link:        'https://wa.me/9311817126',
    detailLink:  '/income-tax-filing',
    accent:      '#F59E0B',
  },
  {
    number:      '03',
    title:       'GST Registration',
    tagline:     'Seamless GST compliance, end to end',
    icon:        <Receipt sx={{ fontSize: 38 }} />,
    description: 'We handle your GST registration accurately and on time, enabling you to collect tax, claim input credits, and expand operations across states without hassle.',
    features:    ['New Registration', 'Input Tax Credit', 'Multi-state Filing', 'GST Returns'],
    link:        'https://wa.me/9311817126',
    detailLink:  '/gst-registration',
    accent:      '#34d399',
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

const ServiceCard = ({ service, index, isActive, onClick, isMobile }) => {
  const [hovered, setHovered] = useState(false);
  const expanded = isActive || (!isMobile && hovered);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.14, ease: 'easeOut' }}
      style={{ flex: 1, minWidth: isMobile ? '100%' : 0 }}
    >
      <motion.div
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        onClick={onClick}
        animate={{
          boxShadow: expanded
            ? `0 0 0 1.5px ${service.accent}, 0 24px 60px rgba(0,0,0,0.4)`
            : '0 2px 20px rgba(0,0,0,0.25)',
        }}
        transition={{ duration: 0.25 }}
        style={{
          background:    'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(12px)',
          borderRadius:  16,
          border:        '1px solid rgba(255,255,255,0.08)',
          cursor:        'pointer',
          overflow:      'hidden',
          height:        '100%',
        }}
      >
        {/* Accent sweep bar */}
        <motion.div
          animate={{ scaleX: expanded ? 1 : 0, originX: 0 }}
          transition={{ duration: 0.3 }}
          style={{ height: 3, background: `linear-gradient(90deg, ${service.accent}, transparent)` }}
        />

        <Box sx={{ p: { xs: 3, md: 3.5 } }}>
          {/* Number + icon */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
            <Typography
              sx={{
                fontFamily:    F.heading,
                fontSize:      { xs: 42, md: 52 },
                fontWeight:    800,
                lineHeight:    1,
                color:         'rgba(255,255,255,0.07)',
                letterSpacing: -2,
                userSelect:    'none',
              }}
            >
              {service.number}
            </Typography>
            <motion.div animate={{ color: expanded ? service.accent : 'rgba(255,255,255,0.5)' }} transition={{ duration: 0.25 }}>
              <Box sx={{ color: 'inherit' }}>{service.icon}</Box>
            </motion.div>
          </Box>

          {/* Title */}
          <Typography sx={{ fontFamily: F.heading, fontSize: { xs: 19, md: 21 }, fontWeight: 700, color: C.white, mb: 0.75, lineHeight: 1.2 }}>
            {service.title}
          </Typography>

          {/* Tagline */}
          <Typography sx={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.45)', mb: 2.5, letterSpacing: 0.2 }}>
            {service.tagline}
          </Typography>

          {/* Expandable content */}
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                key="expanded"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
              >
                <Typography sx={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, mb: 2.5 }}>
                  {service.description}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                  {service.features.map((feat, i) => (
                    <motion.div key={feat} initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }}>
                      <Chip
                        label={feat}
                        size="small"
                        sx={{
                          backgroundColor: `${service.accent}18`,
                          color:           service.accent,
                          border:          `1px solid ${service.accent}40`,
                          fontFamily:      F.body,
                          fontWeight:      500,
                          fontSize:        11,
                        }}
                      />
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Learn more link */}
          <Box
            component={RouterLink}
            to={service.detailLink}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display:        'flex',
              alignItems:     'center',
              gap:            0.75,
              textDecoration: 'none',
              color:          expanded ? service.accent : 'rgba(255,255,255,0.3)',
              transition:     'color 0.25s',
              fontFamily:     F.body,
              fontSize:       13,
              fontWeight:     600,
              letterSpacing:  0.3,
              '&:hover':      { color: service.accent },
            }}
          >
            Learn More
            <motion.div animate={{ x: expanded ? 4 : 0 }} transition={{ duration: 0.25 }}>
              <ArrowForward sx={{ fontSize: 14 }} />
            </motion.div>
          </Box>
        </Box>
      </motion.div>
    </motion.div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const ServicesSection = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeService, setActiveService] = useState(null);

  const handleClick = (index) => {
    if (isMobile) setActiveService(activeService === index ? null : index);
  };

  return (
    <Box
      id="services"
      sx={{
        px: { xs: 2, md: 6 },
        py: { xs: 7, md: 10 },
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px),
          radial-gradient(ellipse at 15% 60%, rgba(29,78,216,0.12) 0%, transparent 55%),
          radial-gradient(ellipse at 85% 20%, rgba(52,211,153,0.08) 0%, transparent 50%),
          linear-gradient(135deg, #0F2040 0%, #1a2e4a 100%)
        `,
        backgroundSize: '48px 48px, 48px 48px, auto, auto, auto',
      }}
    >
      <SectionHeader
        label="What We Offer"
        title={<>Professional Services <Box component="span" sx={{ color: C.amber }}>Built for Growth</Box></>}
        subtitle="Open new doors for your business with expert consulting that keeps you compliant and competitive."
        dark
      />

      <Box
        sx={{
          display:       'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap:           { xs: 2, md: 3 },
          maxWidth:      1100,
          mx:            'auto',
          alignItems:    'stretch',
        }}
      >
        {SERVICES.map((service, index) => (
          <ServiceCard
            key={service.number}
            service={service}
            index={index}
            isActive={activeService === index}
            onClick={() => handleClick(index)}
            isMobile={isMobile}
          />
        ))}
      </Box>

      {isMobile && (
        <Typography
          sx={{
            textAlign:  'center',
            color:      'rgba(255,255,255,0.25)',
            fontFamily: F.body,
            fontSize:   12,
            mt:         3,
          }}
        >
          Tap a card to expand
        </Typography>
      )}

      {/* ── SEO: static keyword-rich content (always rendered in HTML) ── */}
      <Box
        component="section"
        aria-label="Service details"
        sx={{
          maxWidth:  1100,
          mx:        'auto',
          mt:        { xs: 6, md: 8 },
          pt:        { xs: 5, md: 6 },
          borderTop: '1px solid rgba(255,255,255,0.08)',
          display:   'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap:       { xs: 4, md: 5 },
        }}
      >
        {/* Company Registration */}
        <Box>
          <Typography
            component="h2"
            sx={{ fontFamily: F.heading, fontSize: { xs: 17, md: 18 }, fontWeight: 700, color: '#4f8ef7', mb: 1.5 }}
          >
            Company Registration in India
          </Typography>
          <Typography sx={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
            Pracco offers end-to-end company registration services across India, including Private Limited Company
            (Pvt Ltd) incorporation, Limited Liability Partnership (LLP) registration, One Person Company (OPC)
            formation, and Proprietorship setup. Our experts handle MCA filings, DSC, DIN, MOA, AOA, and
            Certificate of Incorporation — so you can launch your new business without delays.
          </Typography>
          <Box component="ul" sx={{ mt: 1.5, pl: 2.5, color: 'rgba(255,255,255,0.4)', fontFamily: F.body, fontSize: 13, lineHeight: 2 }}>
            <li>Private Limited Company registration online</li>
            <li>LLP registration and partnership deed</li>
            <li>One Person Company (OPC) incorporation</li>
            <li>New company incorporation in India</li>
            <li>Startup India registration</li>
          </Box>
        </Box>

        {/* GST Registration */}
        <Box>
          <Typography
            component="h2"
            sx={{ fontFamily: F.heading, fontSize: { xs: 17, md: 18 }, fontWeight: 700, color: '#34d399', mb: 1.5 }}
          >
            GST Registration &amp; Filing Services
          </Typography>
          <Typography sx={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
            Apply for GST registration online and get your GSTIN within days. Pracco manages your complete GST
            compliance — from new GST registration and GSTR-1, GSTR-3B monthly returns to input tax credit (ITC)
            reconciliation and multi-state GST filing. Ideal for businesses, freelancers, and e-commerce sellers
            across India.
          </Typography>
          <Box component="ul" sx={{ mt: 1.5, pl: 2.5, color: 'rgba(255,255,255,0.4)', fontFamily: F.body, fontSize: 13, lineHeight: 2 }}>
            <li>GST registration online (new GSTIN)</li>
            <li>Monthly GST return filing (GSTR-1, GSTR-3B)</li>
            <li>GST annual return (GSTR-9)</li>
            <li>Input tax credit (ITC) claims</li>
            <li>GST compliance for e-commerce sellers</li>
          </Box>
        </Box>

        {/* Income Tax */}
        <Box>
          <Typography
            component="h2"
            sx={{ fontFamily: F.heading, fontSize: { xs: 17, md: 18 }, fontWeight: 700, color: '#F59E0B', mb: 1.5 }}
          >
            Income Tax Return Filing
          </Typography>
          <Typography sx={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
            File your income tax return (ITR) accurately and on time with Pracco. We handle ITR-1 to ITR-6 for
            individuals, salaried employees, self-employed professionals, businesses, NRIs, and expatriates.
            Maximise your refunds, claim all deductions under 80C, 80D, HRA, and stay fully compliant with
            Indian income tax laws.
          </Typography>
          <Box component="ul" sx={{ mt: 1.5, pl: 2.5, color: 'rgba(255,255,255,0.4)', fontFamily: F.body, fontSize: 13, lineHeight: 2 }}>
            <li>ITR filing for salaried individuals</li>
            <li>Income tax return for businesses and firms</li>
            <li>NRI income tax filing India</li>
            <li>Tax planning and deduction advisory</li>
            <li>Tax refund claims and rectification</li>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicesSection;
